import { useDebounce, useStableCallback } from "@prismicio/editor-support/React"
import { Box, LazyRender, Skeleton, Text, Tooltip } from "@prismicio/editor-ui"
import { SimulatorClient } from "@prismicio/simulator"
import { type SharedSliceContent } from "@prismicio/types-internal/lib/content"
import { captureException } from "@sentry/react"
import { useCallback, useEffect, useState } from "react"

import { useRepository } from "../../../../app/services/repository"
import type { DocumentData } from "../../../documentData"
import { useDocument } from "../../../DocumentProvider"
import type { VisualSharedSlice, VisualSlice } from "../sliceTableData"
import { renderAPISlice } from "./slicePreviewAPIRendering"

/**
 * Note: This component requires a key={index} because when an iframe is detached from the document,
 * even briefly via React reconciliation, it reloads.
 * When that happens, the SimulatorClient is stale and we have to re-do the whole loading->connect state workflow.
 */

interface SlicePreviewProps {
  slice: VisualSharedSlice
  onLoad?: () => void
  onError?: () => void
}

export function SlicePreview(props: SlicePreviewProps) {
  const { slice, onLoad, onError } = props
  const { simulatorUrl } = useRepository()

  // All early returns should exist in the Slice Preview layer
  // The Simulator needs to be fully unmounted in order for the client connection to disconnect and re-connect appropriately
  // The iframe element should always be unmounted/remounted together with SimulatorIFrame, never independently
  if (!simulatorUrl) return null
  if (hasIntegrationFieldsContent(slice.content)) return <NoPreviewAvailable />

  return (
    <SimulatorIFrame
      slice={slice}
      simulatorUrl={simulatorUrl}
      onLoad={onLoad}
      onError={onError}
    />
  )
}

interface SimulatorProps {
  slice: VisualSharedSlice
  simulatorUrl: string
  onLoad?: () => void
  onError?: () => void
}

type State =
  | { type: "loading" }
  | { type: "error" }
  | {
      type: "connected"
      client: SimulatorClient
      rendered: boolean
      height?: number
    }

function SimulatorIFrame(props: SimulatorProps) {
  const { slice, simulatorUrl, onLoad } = props

  const onError = useStableCallback(props.onError)

  const { data } = useDocument()

  const [state, setState] = useState<State>({ type: "loading" })
  const stateType = state.type
  const stateClient = state.type === "connected" ? state.client : undefined

  const [availableWidth, setAvailableWidth] = useState(100)
  const [heightFromSimulator, setHeightFromSimulator] = useState(0)

  const height =
    (state.type === "connected" && state.rendered && state.height) ||
    (getKnownSliceHeight(slice, data) ?? loadingDefaultHeight)

  const [iframe, setIframe] = useState<HTMLIFrameElement>()

  const wrapperRef = useCallback((el: HTMLElement | null) => {
    if (el) setAvailableWidth(el.clientWidth)
  }, [])

  const iframeRef = useCallback((el: HTMLIFrameElement | null) => {
    if (el) setIframe(el)
  }, [])

  const downsizing = availableWidth / iframeNativeWidth

  const isLoading =
    state.type === "loading" || (state.type === "connected" && !state.rendered)

  function connect() {
    if (!iframe) return
    let cancelled = false

    const client = new SimulatorClient(
      iframe,
      {
        setSliceZoneSize: (req, res) => {
          setHeightFromSimulator(req.data.rect.height)
          return res.success()
        }
      },
      {
        connectTimeout: 15_000,
        maximumRequestConcurrency: 50
      }
    )

    client
      .connect({ sliceZoneSizeAPI: true })
      .then(() => {
        if (cancelled) return

        setState({ type: "connected", client, rendered: false })

        setTimeout(() => {
          if (!cancelled) onRendered()
        }, previewRenderTime)
      })
      .catch((error) => {
        if (cancelled) return

        captureException(error)
        setState({ type: "error" })
        onError()
      })

    return () => {
      cancelled = true
      client.disconnect()
    }
  }

  function updateContent() {
    if (stateType !== "connected" || !stateClient) return

    // Rendering to API format is very slow (e.g like 50ms for big slices).
    // Also we don't want to do this on every single Editor keystroke to avoid
    // the risk of getting "Too many concurrent requests" Simulator errors.
    // Note: An even better fix would be to stop re-rendering the entire App on each Field key stroke
    // but this is way harder/riskier to fix, see: https://github.com/prismicio/editor/pull/364
    const timeout = setTimeout(() => {
      const sliceContent = renderAPISlice(slice.config, slice.content)

      stateClient.setSliceZone(sliceContent).catch((error) => {
        captureException(error)
        setState({ type: "error" })
      })
    }, 200)

    return () => clearTimeout(timeout)
  }

  const onRendered = useStableCallback(() => {
    if (state.type !== "connected") return

    const height = state.height ?? renderedDefaultHeight

    rememberSliceHeight(slice, data, height)
    setState({ ...state, height, rendered: true })
    onLoad?.()
  })

  const onSimulatorHeightChange = useStableCallback(() => {
    if (state.type !== "connected") return

    const preferredHeight = heightFromSimulator * downsizing
    const height = Math.max(preferredHeight, minHeight)

    if (state.rendered) rememberSliceHeight(slice, data, height)
    setState({ ...state, height })
  })

  useEffect(connect, [iframe, onRendered, onError])
  useEffect(updateContent, [
    stateType,
    stateClient,
    slice.config,
    slice.content
  ])

  useDebounce(heightFromSimulator, 250, onSimulatorHeightChange)

  if (state.type === "error") return <Text color="tomato10">Error</Text>

  return (
    <LazyRender heightEstimate={renderedDefaultHeight} keepLoaded>
      <Box ref={wrapperRef} position="relative" height={height}>
        <iframe
          src={simulatorUrl}
          ref={iframeRef}
          title="Slice Simulator"
          aria-disabled
          style={{
            border: "none",
            width: iframeNativeWidth,
            height: height / downsizing,
            transform: `scale(calc(${downsizing}))`,
            transformOrigin: "top left",
            position: "absolute",
            opacity: isLoading ? "0%" : "100%",
            pointerEvents: "none"
          }}
        />
        {isLoading && (
          <Skeleton
            color="grey1"
            borderRadius={0}
            width="100%"
            height={height}
          />
        )}
      </Box>
    </LazyRender>
  )
}

// The same slice in a different version might have very different content so separate them in the Map.
function getSliceKey(sliceKey: string, data: DocumentData) {
  const version = data.type === "existing" ? data.version.version_id : "new"
  return `${sliceKey}$$$${version}`
}

function getKnownSliceHeight(slice: VisualSlice, data: DocumentData) {
  return sliceHeight.get(getSliceKey(slice.key, data))
}

function rememberSliceHeight(
  slice: VisualSlice,
  data: DocumentData,
  height: number
) {
  sliceHeight.set(getSliceKey(slice.key, data), height)

  // Also remember the last known height for this slice key, in general.
  // This is used to transfer the size from copied to pasted slice.
  sliceHeight.set(slice.key, height)
}

export function rememberPastedSliceHeight(
  copiedSliceKey: string,
  pastedSliceKey: string,
  data: DocumentData
) {
  const height = sliceHeight.get(copiedSliceKey)
  if (!height) return
  sliceHeight.set(getSliceKey(pastedSliceKey, data), height)
}

// When remounting this component, remember its last known size by default
// so that the height doesn't jump before/after while connecting the client.
// This is most useful when doing basic index manipulations like moving a slice or when switching between versions.
const sliceHeight = new Map<string, number>()

// The width of the iframe, before we scale it down.
// This ensures it shows enough details and is rendered as if it was a desktop website.
const iframeNativeWidth = 1500

// Typically, the size taken by the slice if it renders nothing, like a legacy separator margin.
const minHeight = 20

// Until we know more, the preview has this height, in actual pixels.
// A recent simulator version will later communicate its preferred size when it changes.
// Start small so that all slices tend to grow up in size, visually.
const loadingDefaultHeight = minHeight

// When the slice preview is considered rendered, use this if we didn't get any precise measurements yet.
// This can happen with older versions of the simulator or if the Slice component is somehow extremely slow to render.
// Since we assume the slice preview will never resize to fit its content,
// use a fairly big size where we can at least see a good chunk of it.
const renderedDefaultHeight = 140

// It's impossible to know when the slice simulator has finished resizing.
// There are old simulators that won't communicate their size.
// Recent simulators may communicate an initial size then another shortly after after some async elements caused a re-layout, etc.
// Thus, we wait some time for the dust to settle. If we still didn't receive a size from the simulator by this time, we use a default size.
const previewRenderTime = 800

function NoPreviewAvailable() {
  return (
    <Tooltip
      content="Live Preview currently not supported for Slices with Integration Fields."
      side="right"
    >
      <Box padding={{ block: 24 }} justifyContent="center">
        <Text color="grey9" align="center" noSelect>
          No live preview available
        </Text>
      </Box>
    </Tooltip>
  )
}

function hasIntegrationFieldsContent(sliceContent: SharedSliceContent) {
  const repeatedFieldsHasIF = sliceContent.items.some((group) =>
    group.value.some(
      ([_, field]) => field.__TYPE__ === "IntegrationFieldsContent"
    )
  )

  const primaryFieldHasIF = Object.values(sliceContent.primary).some(
    (field) => field.__TYPE__ === "IntegrationFieldsContent"
  )

  return repeatedFieldsHasIF || primaryFieldHasIF
}
